import React, { useRef, useState, useEffect } from "react";
import ReactPlayer from "react-player/vimeo";
import "./Hero.css";

const Hero: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTitleAnimating, setIsTitleAnimating] = useState(false);
  const playerRef = useRef<ReactPlayer>(null);

  // Video data with titles and URLs
  const videoData = [
    {
      title: "The Best Investment You'll Ever Make",
      url: "https://vimeo.com/1080687023/d8f66351a3",
    },
    {
      title: "Why You Need A Coach",
      url: "https://vimeo.com/1080692364/b96446a3f2",
    },
    {
      title: "Why Invest In Quality?",
      url: "https://vimeo.com/1080688770/85e3205e84",
    },
  ];

  const handleReady = () => {
    setIsLoaded(true);
  };

  const handleVideoEnd = () => {
    // Move to the next video when the current one ends
    if (currentVideoIndex < videoData.length - 1) {
      handleTitleTransition(currentVideoIndex + 1);
    } else {
      setIsPlaying(false); // Stop playing when all videos are done
    }
  };

  // Function to handle title animation and video transition
  const handleTitleTransition = (newIndex: number) => {
    setIsTitleAnimating(true);

    // After a short delay, update the video index and restart playing
    setTimeout(() => {
      setCurrentVideoIndex(newIndex);
      setIsPlaying(true);

      // Reset animation state after the animation completes
      setTimeout(() => {
        setIsTitleAnimating(false);
      }, 500);
    }, 500);
  };

  // Function to jump to a specific video when clicking on a dot
  const jumpToVideo = (index: number) => {
    if (index === currentVideoIndex) return;
    handleTitleTransition(index);
  };

  // Effect: initial play state + load Calendly assets once
  useEffect(() => {
    setIsPlaying(false);

    const scriptSrc = "https://assets.calendly.com/assets/external/widget.js";
    const styleHref = "https://assets.calendly.com/assets/external/widget.css";

    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const s = document.createElement("script");
      s.src = scriptSrc;
      s.async = true;
      document.body.appendChild(s);
    }
    if (!document.querySelector(`link[href="${styleHref}"]`)) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = styleHref;
      document.head.appendChild(l);
    }
  }, []);

  return (
    <section className="hero" id="top">
      {/* Video title with animation */}
      <div className={`video-title ${isTitleAnimating ? "animating" : ""}`}>
        <h2>{videoData[currentVideoIndex].title}</h2>
      </div>

      <div className="video-container">
        <div className={`player-wrapper ${isLoaded ? "loaded" : ""}`}>
          <ReactPlayer
            ref={playerRef}
            className="react-player"
            url={videoData[currentVideoIndex].url}
            width="100%"
            height="100%"
            controls={true}
            light={false}
            onReady={handleReady}
            onEnded={handleVideoEnd}
            playing={isPlaying}
          />
        </div>
      </div>

      {/* Video indicator dots */}
      <div className="video-indicators">
        {videoData.map((_, index) => (
          <div
            key={index}
            className={`indicator-dot ${
              index === currentVideoIndex ? "active" : ""
            }`}
            onClick={() => jumpToVideo(index)}
            aria-label={`Go to video ${index + 1}: ${videoData[index].title}`}
          />
        ))}
      </div>

      {/* Popup booking button */}
      <div className="hero-booking-cta">
        <button
          type="button"
          className="book-now-button"
          onClick={() => {
            // @ts-expect-error Calendly global injected by script
            if (window.Calendly?.initPopupWidget) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (window as any).Calendly.initPopupWidget({
                url: "https://calendly.com/mdcwinners/action-plan-call?hide_gdpr_banner=1",
              });
            }
          }}
          aria-label="Open Calendly booking modal"
        >
          Book Now
        </button>
      </div>

      <div
        className="scroll-indicator"
        role="button"
        tabIndex={0}
        onClick={() => {
          const target = document.getElementById("testimonials");
          target?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            const target = document.getElementById("testimonials");
            target?.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
        aria-label="Scroll to testimonials section"
      >
        <div className="scroll-arrow"></div>
        <div className="scroll-arrow"></div>
      </div>
    </section>
  );
};

export default Hero;
